<template>
  <b-modal
    id="building-emergency-modal"
    title="Building Emergency Modal"
    centered
    hide-footer
    size="xl"
    @hidden="reset"
    :no-close-on-esc="true"
    :no-close-on-backdrop="true"
    @shown="onModalShown"
  >
    <template #modal-title>
      <h2 class="m-0">Building Emergency</h2>
    </template>

    <!-- FILTER BUTTONS -->
    <div class="mb-2 d-flex" style="gap:10px;">
      <b-button
        variant="danger"
        :pressed="selectedFilter === 'danger'"
        @click="setFilter('danger')"
      >
        In Danger
      </b-button>

      <b-button
        variant="primary"
        :pressed="selectedFilter === 'primary'"
        @click="setFilter('primary')"
      >
        Exiting
      </b-button>

      <b-button
        variant="success"
        :pressed="selectedFilter === 'success'"
        @click="setFilter('success')"
      >
        Safe
      </b-button>

      <b-button
        variant="secondary"
        @click="setFilter(null)"
      >
        Show All
      </b-button>
    </div>

    <!-- TABLE -->
    <b-table
      responsive="sm"
      :fields="fields"
      :items="filteredData"
      details-td-class="m-0 p-0"
      small
      :per-page="0"
      :tbody-tr-class="rowClass"
    >

      <template #cell(name)="row">
        <b-link
          style="padding-right: 15px; color: black;"
          @click="row.toggleDetails"
          v-if="row.item.user_data.is_peep"
        >
          <feather-icon icon="PlusSquareIcon" />
        </b-link>
        {{ row.item.user_data ? row.item.user_data.name : "" }}
      </template>

      <template #cell(phone_number)="row">
        {{ row.item.user_data ? row.item.user_data.mobile_number : "" }}
      </template>

      <template #cell(building_data)="row">
        {{ row.item.building_data ? row.item.building_data.name : "" }}
      </template>

      <template #cell(is_peep)="row">
        {{ row.item.user_data.is_peep ? 'Yes' : 'No' }}
      </template>

      <template #cell(is_active)="row">
        {{ row.item.is_active === true ? 'Yes' : 'No' }}
      </template>

      <template #cell(in_building)="row">
        {{ row.item.in_building ? 'Yes' : 'No' }}
      </template>

      <template #cell(exit_building)="row">
        {{ row.item.exit_building ? 'Yes' : 'No' }}
      </template>

      <template #cell(reached_assembly_area)="row">
        {{ row.item.reached_assembly_area ? 'Yes' : 'No' }}
      </template>

      <template #row-details="row">
        <b-card bg-variant="light" border-variant="dark">
          <b-table
            responsive="sm"
            :fields="user_flats_fields"
            :items="row.item.nominee_data"
            small
          />
        </b-card>
      </template>

    </b-table>

    <!-- PAGINATION -->
    <div class="row">
      <div class="col-md-11">
        <b-pagination
          size="md"
          :total-rows="totalItems"
          v-model="currentPage"
          :per-page="perPage"
        />
      </div>
      <div class="col-md-1">
        <h4>Total: {{ filteredData.length }}</h4>
      </div>
    </div>

  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import util from "@/util.js";

export default {
  mixins: [util],

  props: {
    floor: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      building_emergency_data: [],
      selectedFilter: null,
      currentPage: 1,
      perPage: 0,
      totalItems: 0,

      fields: [
        { key: "name", label: "Name" },
        { key: "phone_number", label: "Contact Number" },
        { key: "building_data", label: "Building" },
        { key: "is_peep", label: "PEEP Status" },
        { key: "is_active", label: "Response on Alert" },
        { key: "in_building", label: "In Building" },
        { key: "exit_building", label: "Exited From Building" },
        { key: "reached_assembly_area", label: "Reached Assembly Area" },
        { key: "updated_at", label: "Last Update At" },
      ],
    };
  },

  computed: {
    ...mapGetters({
      getLoggedInUser: "appData/getLoggedInUser",
    }),

    // FILTERED DATA
    filteredData() {
      if (!this.selectedFilter) {
        return this.building_emergency_data;
      }

      return this.building_emergency_data.filter(item => {

        // DANGER
        if (this.selectedFilter === "danger") {
          return (
            item.in_building === true &&
            (item.exit_building === false || item.exit_building === null) &&
            (item.reached_assembly_area === false || item.reached_assembly_area === null)
          );
        }

        // PRIMARY
        if (this.selectedFilter === "primary") {
          return (
            item.in_building === true &&
            item.exit_building === true &&
            (item.reached_assembly_area === false || item.reached_assembly_area === null)
          );
        }

        // SUCCESS
        if (this.selectedFilter === "success") {
          return (
            item.in_building === true &&
            item.reached_assembly_area === true
          );
        }

        return true;
      });
    },
  },

  methods: {
    ...mapActions({
      getFlatUsers: "appData/getFlatUsers",
    }),

    async onModalShown() {
      try {
        const res = await this.getFlatUsers({
          pageNumber: this.currentPage,
          building: this.floor.building_id,
          floor: this.floor.floor_id,
        });

        this.building_emergency_data = res.data.results;
        this.totalItems = res.data.count;
        this.perPage = res.data.per_page;

      } catch (error) {
        this.displayError(error);
      }
    },

    setFilter(filter) {
      this.selectedFilter = filter;
    },

    // ROW COLOR LOGIC
    rowClass(item, type) {
      if (!item || type !== "row") return;

      // DANGER
      if (
        item.in_building === true &&
        (item.exit_building === false || item.exit_building === null) &&
        (item.reached_assembly_area === false || item.reached_assembly_area === null)
      ) return "table-danger";

      // PRIMARY
      if (
        item.in_building === true &&
        item.exit_building === true &&
        (item.reached_assembly_area === false || item.reached_assembly_area === null)
      ) return "table-primary";

      // SUCCESS
      if (
        item.in_building === true &&
        item.reached_assembly_area === true
      ) return "table-success";
    },

    reset() {
      this.selectedFilter = null;
    },
  },
};
</script>